/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Subscriptions Hooks - React Query for Organization Subscriptions
 * ============================================================================
 *
 * Custom hooks for managing organization module subscriptions using React Query.
 * Provides CRUD operations for subscription items and management actions.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionsService } from '@/services/superadmin/subscriptions.service';
import {
  SubscriptionSummary,
  ModuleSubscriptionItem,
  CreateSubscriptionRequest,
  AddModuleRequest,
  ChangeTierRequest,
} from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';
import { organizationKeys } from './useOrganizations';
import { orgSettingsKeys } from './useOrganizationSettings';

// Query keys for cache management
export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  org: (orgId: string) => [...subscriptionKeys.all, 'org', orgId] as const,
  stats: (orgId: string) => [...subscriptionKeys.all, 'stats', orgId] as const,
};

/**
 * Hook to fetch subscription summary for an organization.
 */
export function useOrgSubscription(orgId: string) {
  return useQuery<SubscriptionSummary, ApiError>({
    queryKey: subscriptionKeys.org(orgId),
    queryFn: () => subscriptionsService.getSubscription(orgId),
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch subscription statistics for an organization.
 */
export function useOrgSubscriptionStats(orgId: string) {
  return useQuery<Record<string, unknown>, ApiError>({
    queryKey: subscriptionKeys.stats(orgId),
    queryFn: () => subscriptionsService.getSubscriptionStats(orgId),
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for creating a subscription for an organization.
 */
export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionSummary, ApiError, { orgId: string; data: CreateSubscriptionRequest }>({
    mutationFn: ({ orgId, data }) => subscriptionsService.createSubscription(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
      // Also invalidate org and settings queries since currency may have changed
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orgSettingsKeys.byOrg(orgId) });
    },
  });
}

/**
 * Hook for updating an existing subscription for an organization.
 */
export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionSummary, ApiError, { orgId: string; data: CreateSubscriptionRequest }>({
    mutationFn: ({ orgId, data }) => subscriptionsService.updateSubscription(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
      // Also invalidate org and settings queries since currency may have changed
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orgSettingsKeys.byOrg(orgId) });
    },
  });
}

/**
 * Hook for adding a module to an organization's subscription.
 */
export function useAddModule() {
  const queryClient = useQueryClient();

  return useMutation<ModuleSubscriptionItem, ApiError, { orgId: string; data: AddModuleRequest }>({
    mutationFn: ({ orgId, data }) => subscriptionsService.addModule(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
    },
  });
}

/**
 * Hook for changing a module's tier.
 */
export function useChangeTier() {
  const queryClient = useQueryClient();

  return useMutation<
    ModuleSubscriptionItem,
    ApiError,
    { orgId: string; moduleCode: string; data: ChangeTierRequest }
  >({
    mutationFn: ({ orgId, moduleCode, data }) =>
      subscriptionsService.changeTier(orgId, moduleCode, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
    },
  });
}

/**
 * Hook for updating user count for a per-user module.
 */
export function useUpdateModuleUserCount() {
  const queryClient = useQueryClient();

  return useMutation<
    ModuleSubscriptionItem,
    ApiError,
    { orgId: string; moduleCode: string; userCount: number }
  >({
    mutationFn: ({ orgId, moduleCode, userCount }) =>
      subscriptionsService.updateUserCount(orgId, moduleCode, userCount),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
    },
  });
}

/**
 * Hook for removing a module from an organization's subscription.
 */
export function useRemoveModule() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { orgId: string; moduleCode: string; reason?: string }>({
    mutationFn: ({ orgId, moduleCode, reason }) =>
      subscriptionsService.removeModule(orgId, moduleCode, reason),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
    },
  });
}

/**
 * Hook for converting a trial module to active subscription.
 */
export function useConvertTrial() {
  const queryClient = useQueryClient();

  return useMutation<ModuleSubscriptionItem, ApiError, { orgId: string; moduleCode: string }>({
    mutationFn: ({ orgId, moduleCode }) => subscriptionsService.convertTrial(orgId, moduleCode),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
    },
  });
}

// ============================================================================
// Subscription Status Management Hooks
// ============================================================================

/**
 * Hook for suspending (disabling) a subscription.
 */
export function useSuspendSubscription() {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionSummary, ApiError, { orgId: string; reason?: string }>({
    mutationFn: ({ orgId, reason }) => subscriptionsService.suspendSubscription(orgId, reason),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for reactivating a suspended subscription.
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionSummary, ApiError, string>({
    mutationFn: (orgId) => subscriptionsService.reactivateSubscription(orgId),
    onSuccess: (_, orgId) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for toggling a module feature on/off.
 */
export function useToggleModuleFeature() {
  const queryClient = useQueryClient();

  return useMutation<
    Record<string, boolean>,
    ApiError,
    { orgId: string; moduleCode: string; featureCode: string; enabled: boolean }
  >({
    mutationFn: ({ orgId, moduleCode, featureCode, enabled }) =>
      subscriptionsService.toggleFeature(orgId, moduleCode, featureCode, enabled),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
    },
  });
}

/**
 * Hook for extending subscription period.
 */
export function useExtendSubscription() {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionSummary, ApiError, { orgId: string; days: number }>({
    mutationFn: ({ orgId, days }) => subscriptionsService.extendSubscription(orgId, days),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.stats(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for updating the grace period for an organization's subscription.
 */
export function useUpdateGracePeriod() {
  const queryClient = useQueryClient();

  return useMutation<number, ApiError, { orgId: string; days: number }>({
    mutationFn: ({ orgId, days }) => subscriptionsService.updateGracePeriod(orgId, days),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
    },
  });
}
